import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"

import { cn } from "@/lib/utils"

const Popover = PopoverPrimitive.Root

const PopoverTrigger = PopoverPrimitive.Trigger

const PopoverAnchor = PopoverPrimitive.Anchor

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
    /** Render content inside a Portal (default: true). Set false for nested popovers to avoid flashing/repositioning on unmount. */
    portalled?: boolean;
  }
>(({ className, align = "center", side = "bottom", sideOffset = 4, portalled = true, ...props }, ref) => {
  const content = (
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      side={side}
      sideOffset={sideOffset}
      collisionPadding={16}
      avoidCollisions={true}
      sticky="partial"
      onOpenAutoFocus={(e) => e.preventDefault()}
      onCloseAutoFocus={(e) => e.preventDefault()}
      className={cn(
        "z-[9999] w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none pointer-events-auto",
        // Only animate-in on open, no exit animation to prevent flicker/teleportation
        "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-150",
        className
      )}
      {...props}
    />
  );

  if (!portalled) return content;

  // IMPORTANT: In native fullscreen mode, only descendants of the fullscreen element are visible.
  // Radix Popover portals to document.body by default, which makes popovers disappear.
  // We portal into the fullscreen element when present.
  const container = typeof document !== "undefined" ? (document.fullscreenElement ?? undefined) : undefined;

  return <PopoverPrimitive.Portal container={container}>{content}</PopoverPrimitive.Portal>;
})
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export { Popover, PopoverTrigger, PopoverAnchor, PopoverContent }
